import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { describeError, useToast } from "../components/toast";
import AddBusinessIcon from "@mui/icons-material/AddBusinessOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { IconButton, Tooltip } from "@mui/material";
import CompanyEnergyDialog from "../components/CompanyEnergyDialog";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CONTENT_OWNER_KIND_OPTIONS,
  foyerApi,
  type CompanySummary,
  type CompanyTypeValue,
  type ContentOwnerKindValue,
  type ContentSupplierRow,
  type PremiseSummary,
} from "../http/foyer";
import CompanySettingsDialog from "../components/companies/CompanySettingsDialog";
import { usePermission } from "../auth/access";
import DangerConfirmDialog from "../components/DangerConfirmDialog";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { errorCode, friendlyError } from "../utils/apiError";

type CompanyType = CompanyTypeValue;

// A CONTENT_OWNER is a studio, label group or creator (Sigge, 2026-10-19):
// it holds content feeds, never premises; its users sign in to the
// content-owner view.
const COMPANY_TYPES: readonly CompanyType[] = [
  "CUSTOMER",
  "RESELLER",
  "SYSTEM_INTEGRATOR",
  "ADVERTISER",
  "CONTENT_OWNER",
];

const isCompanyType = (value: string): value is CompanyType =>
  (COMPANY_TYPES as readonly string[]).includes(value);

/**
 * Top-level list of all Companies (tenants). Click a row to drill in.
 * Admins can create new tenants via the dialog.
 */
export default function CompaniesPage() {
  const navigate = useNavigate();
  const canManage = usePermission("companies", "administrate", "nenda");
  const [companies, setCompanies] = useState<CompanySummary[] | null>(null);
  const [premises, setPremises] = useState<PremiseSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleting, setDeleting] = useState<CompanySummary | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [energyFor, setEnergyFor] = useState<CompanySummary | null>(null);
  // `?settings=<id>` opens a company's settings - the Content feeds list
  // links a feed's company here.
  const [params, setParams] = useSearchParams();
  const settingsId = params.get("settings");
  const settingsFor =
    (settingsId && companies?.find((c) => c.id === settingsId)) || null;
  const openSettings = (c: CompanySummary | null) => {
    const next = new URLSearchParams(params);
    if (c) next.set("settings", c.id);
    else next.delete("settings");
    setParams(next, { replace: true });
  };
  const toast = useToast();
  const { t } = useTranslation(["users", "common"]);
  const typeLabel = (type: string) =>
    isCompanyType(type) ? t(`companiesPage.types.${type}`) : type;

  async function refresh() {
    try {
      const [c, p] = await Promise.all([
        foyerApi.listCompanies(),
        foyerApi.listPremises(),
      ]);
      setCompanies(c);
      setPremises(p);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  const countByCompany = new Map<string, number>();
  for (const p of premises ?? []) {
    if (!p.companyId) continue;
    countByCompany.set(p.companyId, (countByCompany.get(p.companyId) ?? 0) + 1);
  }
  const unassigned = (premises ?? []).filter((p) => !p.companyId).length;

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("companiesPage.eyebrow")}
        title={t("companiesPage.title")}
        subtitle={t("companiesPage.subtitle")}
        actions={
          canManage && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddBusinessIcon />}
              onClick={() => setCreateOpen(true)}
              data-testid="companies-new"
            >
              {t("companiesPage.newCompany")}
            </Button>
          )
        }
      />

      {err && (
        <Typography variant="body2" color="error">
          {err}
        </Typography>
      )}

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" stickyHeader data-testid="companies-list">
            <TableHead>
              <TableRow>
                <TableCell>{t("common:labels.name")}</TableCell>
                <TableCell>{t("common:labels.type")}</TableCell>
                <TableCell align="right">
                  {t("companiesPage.columns.premises")}
                </TableCell>
                <TableCell sx={{ width: 140 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {companies?.map((c) => {
                const n = countByCompany.get(c.id) ?? 0;
                const contentOwner = c.type === "CONTENT_OWNER";
                // A content owner has no premises to drill into: its row opens
                // its settings (feeds, login, storefront) instead.
                const goto = () =>
                  contentOwner
                    ? openSettings(c)
                    : navigate(`/premises?company=${encodeURIComponent(c.id)}`);
                return (
                  <TableRow
                    key={c.id}
                    hover
                    tabIndex={0}
                    role="button"
                    aria-label={
                      contentOwner
                        ? t("companiesPage.rowSettings", { name: c.name })
                        : t("companiesPage.rowPremises", { name: c.name })
                    }
                    onClick={goto}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        goto();
                      }
                    }}
                    sx={{ cursor: "pointer" }}
                    data-testid={`company-row-${c.id}`}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "fontWeightSemibold" }}
                      >
                        {c.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={typeLabel(c.type)}
                        size="small"
                        variant="outlined"
                        color={contentOwner ? "secondary" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {contentOwner
                        ? t("common:labels.emptyValue")
                        : t("companiesPage.premiseCount", { count: n })}
                    </TableCell>
                    <TableCell
                      align="right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title={t("companiesPage.settingsTooltip")}>
                        <IconButton
                          size="small"
                          aria-label={t("companiesPage.settingsFor", {
                            name: c.name,
                          })}
                          onClick={() => openSettings(c)}
                          data-testid={`company-settings-${c.id}`}
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!contentOwner && (
                        <Tooltip title={t("companiesPage.chainEnergy")}>
                          <IconButton
                            size="small"
                            aria-label={t("companiesPage.energyFor", {
                              name: c.name,
                            })}
                            onClick={() => setEnergyFor(c)}
                            data-testid={`company-energy-${c.id}`}
                          >
                            <BoltOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canManage && (
                        <Tooltip title={t("companiesPage.deleteCompany")}>
                          <IconButton
                            size="small"
                            aria-label={t("companiesPage.deleteNamed", {
                              name: c.name,
                            })}
                            onClick={() => {
                              setDeleting(c);
                            }}
                            data-testid={`company-delete-${c.id}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {unassigned > 0 && (
                <TableRow
                  hover
                  onClick={() => navigate(`/premises?company=unassigned`)}
                  sx={{ cursor: "pointer" }}
                  data-testid="company-row-unassigned"
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: "italic", color: "text.secondary" }}
                    >
                      {t("companiesPage.unassigned")}
                    </Typography>
                  </TableCell>
                  <TableCell />
                  <TableCell align="right">
                    {t("companiesPage.premiseCount", { count: unassigned })}
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
              {companies?.length === 0 && unassigned === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box
                      sx={{
                        py: 5,
                        textAlign: "center",
                        color: "text.secondary",
                      }}
                    >
                      <Typography variant="body2">
                        {t("companiesPage.noCompanies")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <CreateCompanyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(c, feed) => {
          setCreateOpen(false);
          toast.success(t("companiesPage.companyCreated", { name: c.name }));
          if (feed) {
            toast.success(
              feed.how === "created"
                ? t("companiesPage.feedCreatedFor", {
                    feed: feed.name,
                    company: c.name,
                  })
                : t("companiesPage.feedConnectedTo", {
                    feed: feed.name,
                    company: c.name,
                  })
            );
          }
          refresh();
        }}
        onError={(msg) => toast.error(msg)}
      />

      {settingsFor ? (
        <CompanySettingsDialog
          company={settingsFor}
          onClose={() => openSettings(null)}
        />
      ) : null}

      <CompanyEnergyDialog
        open={!!energyFor}
        companyId={energyFor?.id ?? null}
        companyName={energyFor?.name ?? ""}
        onClose={() => setEnergyFor(null)}
      />

      <DangerConfirmDialog
        open={!!deleting}
        title={t("companiesPage.deleteCompany")}
        entityName={deleting?.name ?? ""}
        body={
          <Typography variant="body2" color="text.secondary">
            {t("companiesPage.deleteBody")}
          </Typography>
        }
        busy={deleteBusy}
        onCancel={() => {
          if (deleteBusy) return;
          setDeleting(null);
        }}
        onConfirm={async () => {
          if (!deleting) return;
          setDeleteBusy(true);
          try {
            await foyerApi.deleteCompany(deleting.id);
            toast.success(
              t("companiesPage.companyDeleted", { name: deleting.name })
            );
            setDeleting(null);
            refresh();
          } catch (e) {
            // The dialog stays open so the delete can be retried.
            toast.error(
              describeError(t, "deleteNamed", e, { name: deleting.name })
            );
          } finally {
            setDeleteBusy(false);
          }
        }}
      />
    </PageContainer>
  );
}

/** How a new company gets its first content feed, if at all. */
type FeedChoice = "none" | "create" | "connect";

function CreateCompanyDialog({
  open,
  onClose,
  onCreated,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  /** `feed` is set when a content feed was attached as part of the create. */
  onCreated: (
    c: CompanySummary,
    feed?: { how: "created" | "connected"; name: string }
  ) => void;
  onError: (msg: string) => void;
}) {
  const { t } = useTranslation(["users", "common", "content"]);
  // Two hooks, both always called: `&&` would skip the second one.
  const canDistribute = usePermission(
    "distribution/suppliers",
    "administrate",
    "nenda"
  );
  const canTrust = usePermission("supplier-trust", "administrate", "nenda");
  const canManageFeeds = canDistribute && canTrust;
  const [name, setName] = useState("");
  const [type, setType] = useState<CompanyType>("CUSTOMER");
  const [kind, setKind] = useState<ContentOwnerKindValue | "">("");
  const [contactEmail, setContactEmail] = useState("");
  const [feedChoice, setFeedChoice] = useState<FeedChoice>("none");
  const [feedName, setFeedName] = useState("");
  const [feedNameEdited, setFeedNameEdited] = useState(false);
  const [connectId, setConnectId] = useState("");
  const [unattached, setUnattached] = useState<ContentSupplierRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setType("CUSTOMER");
      setKind("");
      setContactEmail("");
      setFeedChoice("none");
      setFeedName("");
      setFeedNameEdited(false);
      setConnectId("");
      setNameError(null);
      if (canManageFeeds) {
        foyerApi.listContentSuppliers().then(
          (r) => setUnattached(r.suppliers.filter((s) => !s.company)),
          () => setUnattached([])
        );
      }
    }
  }, [open, canManageFeeds]);

  const contentOwner = type === "CONTENT_OWNER";
  // A content owner usually arrives with its feed; anyone else rarely does.
  useEffect(() => {
    setFeedChoice(contentOwner && canManageFeeds ? "create" : "none");
  }, [contentOwner, canManageFeeds]);

  const ready =
    name.trim() !== "" &&
    (!contentOwner || kind !== "") &&
    (feedChoice !== "create" ||
      (feedNameEdited ? feedName : name).trim() !== "") &&
    (feedChoice !== "connect" || connectId !== "");

  async function submit() {
    setBusy(true);
    setNameError(null);
    let created: CompanySummary;
    try {
      created = await foyerApi.createCompany({
        name,
        type,
        ...(contentOwner && kind
          ? {
              contentOwner: {
                kind,
                contactEmail: contactEmail.trim() || undefined,
              },
            }
          : {}),
      });
    } catch (e) {
      if (errorCode(e) === "company_already_exists") {
        setNameError(friendlyError(t, e));
      } else {
        onError(
          describeError(t, "createNamedCompany", e, { name: name.trim() })
        );
      }
      setBusy(false);
      return;
    }
    // The feed is a second write (to the shared catalogue, for a new one).
    // If it fails the company still exists - say so, and the feed can be
    // added from the company's settings.
    let feed: { how: "created" | "connected"; name: string } | undefined;
    try {
      if (feedChoice === "create") {
        const feedLabel = (feedNameEdited ? feedName : name).trim();
        await foyerApi.createContentSupplier({
          name: feedLabel,
          companyId: created.id,
        });
        feed = { how: "created", name: feedLabel };
      } else if (feedChoice === "connect" && connectId) {
        await foyerApi.setContentSupplierCompany(connectId, created.id);
        feed = {
          how: "connected",
          name:
            unattached.find((f) => f.id === connectId)?.name ??
            t("companiesPage.create.feedFallbackName"),
        };
      }
    } catch (e) {
      onError(
        t("companiesPage.create.feedNotCreated", {
          company: created.name,
          reason: friendlyError(t, e),
        })
      );
    }
    setBusy(false);
    onCreated(created, feed);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("companiesPage.newCompany")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("common:labels.name")}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameError(null);
            }}
            autoFocus
            required
            size="small"
            error={!!nameError}
            helperText={nameError ?? undefined}
            inputProps={{ "data-testid": "company-name" }}
          />
          <TextField
            select
            label={t("common:labels.type")}
            value={type}
            onChange={(e) => setType(e.target.value as CompanyType)}
            size="small"
            inputProps={{ "data-testid": "company-type" }}
            helperText={
              contentOwner
                ? t("companiesPage.create.contentOwnerHelp")
                : undefined
            }
          >
            {COMPANY_TYPES.map((key) => (
              <MenuItem key={key} value={key}>
                {t(`companiesPage.types.${key}`)}
              </MenuItem>
            ))}
          </TextField>

          {contentOwner ? (
            <>
              <TextField
                select
                label={t("companiesPage.create.kindLabel")}
                value={kind}
                onChange={(e) =>
                  setKind(e.target.value as ContentOwnerKindValue)
                }
                size="small"
                required
                error={kind === ""}
                helperText={t("companiesPage.create.kindHelp")}
                inputProps={{ "data-testid": "company-owner-kind" }}
              >
                {CONTENT_OWNER_KIND_OPTIONS.map((k) => (
                  <MenuItem key={k.value} value={k.value}>
                    {t(`content:contentFeedDialogs.ownerKind.${k.value}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t("companiesPage.create.contactEmail")}
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                size="small"
                helperText={t("companiesPage.create.contactEmailHelp")}
                inputProps={{ "data-testid": "company-owner-contact" }}
              />
            </>
          ) : null}

          {canManageFeeds ? (
            <>
              <TextField
                select
                label={t("companiesPage.create.feedLabel")}
                value={feedChoice}
                onChange={(e) => setFeedChoice(e.target.value as FeedChoice)}
                size="small"
                inputProps={{ "data-testid": "company-feed-choice" }}
                helperText={t("companiesPage.create.feedHelp")}
              >
                <MenuItem value="none">
                  {t("companiesPage.create.feedNone")}
                </MenuItem>
                <MenuItem value="create">
                  {t("companiesPage.create.feedCreate")}
                </MenuItem>
                <MenuItem value="connect">
                  {t("companiesPage.create.feedConnect")}
                </MenuItem>
              </TextField>
              {feedChoice === "create" ? (
                <TextField
                  label={t("companiesPage.create.feedName")}
                  value={feedNameEdited ? feedName : name}
                  onChange={(e) => {
                    setFeedName(e.target.value);
                    setFeedNameEdited(true);
                  }}
                  size="small"
                  required
                  helperText={t("companiesPage.create.feedNameHelp")}
                  inputProps={{ "data-testid": "company-feed-name" }}
                />
              ) : null}
              {feedChoice === "connect" ? (
                <TextField
                  select
                  label={t("common:labels.feed")}
                  value={connectId}
                  onChange={(e) => setConnectId(e.target.value)}
                  size="small"
                  required
                  helperText={t("companiesPage.create.connectHelp")}
                  inputProps={{ "data-testid": "company-feed-connect" }}
                >
                  {unattached.length === 0 ? (
                    <MenuItem value="" disabled>
                      {t("companiesPage.create.noUnattachedFeeds")}
                    </MenuItem>
                  ) : null}
                  {unattached.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {t("companiesPage.create.feedOption", {
                        name: f.name,
                        id: f.id,
                      })}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common:buttons.cancel")}</Button>
        <Button
          variant="contained"
          disabled={busy || !ready}
          onClick={submit}
          data-testid="company-create"
        >
          {busy ? t("common:states.creating") : t("common:buttons.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
